import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiFileText, FiAlertCircle, FiClock } from 'react-icons/fi';
import type { IconType } from 'react-icons';
import type { DashboardWorkQueue, WorkQueueGroupKey, WorkQueueMetricKey } from '../types/dashboard';
import {
  WORK_QUEUE_GROUP_CONFIG,
  WORK_QUEUE_METRIC_LABELS,
  WORK_QUEUE_METRIC_PATH_SUFFIX,
} from '../types/dashboard';

interface Props {
  workQueue: DashboardWorkQueue;
  scopeSegment: string;
  scopeId: number;
}

const metricIconMap: Record<WorkQueueMetricKey, IconType> = {
  draft: FiFileText,
  needs_attention: FiAlertCircle,
  awaiting_my_approval: FiClock,
};

const metricColorMap: Record<WorkQueueMetricKey, 'primary' | 'warning' | 'info'> = {
  draft: 'primary',
  needs_attention: 'warning',
  awaiting_my_approval: 'info',
};

const GROUP_ORDER: WorkQueueGroupKey[] = [
  'activity',
  'department_reports',
  'pastor_report',
  'finance_statistics',
];

const WorkQueueSection: React.FC<Props> = ({ workQueue, scopeSegment, scopeId }) => {
  const navigate = useNavigate();
  const basePath = `/${scopeSegment}/${scopeId}`;

  const groups = GROUP_ORDER.filter((key) => workQueue[key] !== undefined);

  if (!groups.length) return null;

  return (
    <div className="dashboard-work-queue-sections mb-4">
      {groups.map((groupKey) => {
        const group = workQueue[groupKey]!;
        const config = WORK_QUEUE_GROUP_CONFIG[groupKey];

        return (
          <section key={groupKey} className="dashboard-work-queue-section mb-4">
            <header className="dashboard-work-queue-section__header mb-2">
              <h2 className="dashboard-work-queue-section__title h6 mb-0">{config.title}</h2>
              <p className="dashboard-work-queue-section__subtitle text-muted small mb-0">
                {config.subtitle}
              </p>
            </header>
            <div className="dashboard-stats dashboard-stats--auto-fill">
              {config.metrics.map((metricKey) => {
                if (typeof group[metricKey] !== 'number') return null;

                const value = group[metricKey] as number;
                const Icon = metricIconMap[metricKey] ?? FiCheckCircle;
                const color = metricColorMap[metricKey] ?? 'primary';
                const path = `${basePath}${config.pathPrefix}${WORK_QUEUE_METRIC_PATH_SUFFIX[metricKey]}`;
                const isEmpty = value === 0;

                const content = (
                  <>
                    <span className={`dashboard-stat-card__icon dashboard-stat-card__icon--${color}`}>
                      <Icon size={20} />
                    </span>
                    <div className="dashboard-stat-card__content">
                      <span className="dashboard-stat-card__value">{value}</span>
                      <span className="dashboard-stat-card__label">
                        {WORK_QUEUE_METRIC_LABELS[metricKey]}
                      </span>
                    </div>
                  </>
                );

                if (isEmpty) {
                  return (
                    <div
                      key={metricKey}
                      className="dashboard-stat-card dashboard-stat-card--disabled"
                      aria-disabled="true"
                    >
                      {content}
                    </div>
                  );
                }

                return (
                  <a
                    key={metricKey}
                    href={path}
                    className="dashboard-stat-card dashboard-stat-card--clickable"
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault();
                      navigate(path);
                    }}
                  >
                    {content}
                  </a>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default WorkQueueSection;
